import { FormApi } from 'final-form';
import { observer } from 'mobx-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { getLocationsTaskFactory } from '~admin/interactor/location/perform-get-locations';
import { getSeatConfigurationsTaskFactory } from '~admin/interactor/location/perform-get-seat-configurations';
import { MediaData } from '~admin/model/media-data';
import { useCreateEditEventFlowStore } from '~admin/store/create-edit-event-flow-store-provider';
import { streaming } from '~admin/view/components/details/event-details/event-details.helper';
import { EventLocationDropdown } from '~admin/view/components/details/event-details/event-location-details/event-location-dropdown.component';
import { SeatConfigurationsDropdown } from '~admin/view/components/details/event-details/event-location-details/seat-configurations-dropdown.component';
import { EventFormData } from '~admin/view/components/details/event-form-data';
import { MediaField } from '~admin/view/components/ui/form/fields/field-media.component';
import { SelectField } from '~admin/view/components/ui/form/fields/field-select.component';
import { AccessibilityType } from '~client/model/accessibility-type';
import { MediaType } from '~client/model/media-type';
import { Box } from '~common/components/box.component';
import { Column } from '~common/components/column.component';
import { optionEnumeratorMapper } from '~common/components/select.helper';
import { useTask } from '~common/hook/use-task';
import { AddButton } from '~admin/view/components/ui/button/add-button.component';
import { useModalControls } from '~common/hook/use-modal-controls';
import { SeatConfigurationData } from '~admin/model/seat-configuration-data';
import { AddSeatConfigurationOverlay } from '~admin/view/components/details/event-details/event-location-details/add-seat-configuration-overlay.component';

export interface EventLocationFormData {
  eventLocationId: number;
  seatConfigurationId: number;
  eventAccessibilityType: AccessibilityType;
  seatMapMedia?: MediaData;
}

interface Props {
  form: FormApi<EventFormData>;
}

export const EventLocationDetails: React.FunctionComponent<Props> = observer(({ form }) => {
  const { t } = useTranslation();
  const [overlayShown, showOverlay, hideOverlay] = useModalControls();
  const getLocationsTask = useTask(getLocationsTaskFactory);
  const getSeatConfigurations = useTask(getSeatConfigurationsTaskFactory);
  const createEventFlowStore = useCreateEditEventFlowStore();
  const { eventLocationId, isEdit, eventAccessibilityType } = createEventFlowStore;

  return (
    <Container>
      <Box>
        <Row>
          <RestrictBox>
            <EventLocationDropdown
              name={'eventLocationId'}
              label={t('createEvent.eventDetails.location.eventLocation')}
              task={getLocationsTask}
              disabled={isEdit}
            />
          </RestrictBox>
          <RestrictBox>
            <SeatConfigurationsDropdown
              name={'seatConfigurationId'}
              label={t('createEvent.eventDetails.location.seatConfiguration')}
              task={getSeatConfigurations}
              eventLocationId={eventLocationId}
              onEmpty={(): void => {
                form.change('seatConfigurationId', null);
              }}
              disabled={isEdit}
            />
            <StyledAddButton onClick={showOverlay} disabled={isEdit} />
            {overlayShown && (
              <AddSeatConfigurationOverlay
                eventLocationId={eventLocationId}
                onCancel={hideOverlay}
                onSeatConfigurationCreated={(seatConfiguration: SeatConfigurationData): void => {
                  getSeatConfigurations(eventLocationId);
                  form.change('seatConfigurationId', seatConfiguration.id);
                  hideOverlay();
                }}
              />
            )}
          </RestrictBox>
          <RestrictBox>
            <SelectField
              name={'eventAccessibilityType'}
              label={t('createEvent.eventDetails.options.accessibilityType.fieldLabel')}
              options={optionEnumeratorMapper(
                AccessibilityType,
                t,
                'createEvent.eventDetails.options.accessibilityType.enum',
              )}
              disabled={isEdit}
            />
          </RestrictBox>
        </Row>
        <MediaField
          name={'seatMapMedia'}
          label={t('createEvent.eventDetails.location.seatMapMedia')}
          mediaType={MediaType.SEATMAP}
          onMediaSelected={(media: MediaData | null): void => {
            form.change('seatMapMedia', media);
          }}
          disabled={isEdit || streaming(eventAccessibilityType)}
        />
      </Box>
    </Container>
  );
});

const Container = styled(Column)`
  flex: 1 1 auto;
  border: 2px solid ${(props): string => props.theme.colors.gray.v3};
  border-radius: 16px;
  padding: 16px;
`;

const Row = styled(Column)`
  flex: 1 1 auto;
  margin-right: 24px;
`;

const RestrictBox = styled(Box)`
  flex: 1 1 auto;
  margin: 0 12px;
  justify-content: center;
`;

const StyledAddButton = styled(AddButton)`
  && {
    margin-left: 16px;
  }
`;



// WEBPACK FOOTER //
// ./view/components/details/event-details/event-location-details.tsx